"use client"

import React, { useContext, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { OrganizationContext } from "@/contexts/organization"
import { SiteConfigContext } from "@/contexts/site-config"
import { useSession } from "next-auth/react"
import { FormattedMessage, FormattedNumber } from "react-intl"

import { subscriptionPlans } from "@/config/site"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import ContactUs from "@/components/contact-us"

const paymentUrls = {
  monthly: {
    FREE: "",
    BASIC:
      "https://42x.lemonsqueezy.com/checkout/buy/67a6f0fa-7e70-454f-83ce-922203844384",
    BUSINESS:
      "https://42x.lemonsqueezy.com/checkout/buy/8ad3c579-13d5-43df-ac88-4f701832035f",
  },
  yearly: {
    FREE: "",
    BASIC:
      "https://42x.lemonsqueezy.com/checkout/buy/cbadcdcc-158b-42ae-8474-9fcd6e3955ff",
    BUSINESS:
      "https://42x.lemonsqueezy.com/checkout/buy/9ea7ca82-49d0-4c3d-abdd-570f197fdb90",
  },
}

interface SubscriptionPlansMainProps {
  isOpen?: boolean
  onOpenChange?: (value: boolean) => void
}

export function SubscriptionPlansMain({
  isOpen,
  onOpenChange,
}: SubscriptionPlansMainProps) {
  const { push } = useRouter()
  const { selectedOrganization } = useContext(OrganizationContext)
  const { state: siteConfig, dispatch: dispatchConfig } =
    useContext(SiteConfigContext)

  const { selectedPlan } = siteConfig
  const [isYearly, setIsYearly] = useState(false)
  const [contactUsOpen, setContactUsOpen] = useState(false)
  const { data: session } = useSession()
  const planKeysRaw = Object.keys(subscriptionPlans) as Array<
    keyof typeof subscriptionPlans
  >
  // remove FREE plan
  const planKeys = planKeysRaw.filter((key) => key !== "FREE") as Array<
    keyof typeof subscriptionPlans
  >

  const currentPlan =
    selectedOrganization?.subscription_plan ||
    session?.user?.UserOrganizationRole[0].Organization.subscription_plan

  const handlePlanSelection = (key: keyof typeof subscriptionPlans) => {
    // Step 1: Update the SiteConfigContext
    dispatchConfig({
      type: "SET_SELECTED_PLAN",
      payload: {
        name: key,
        billing_cycle: isYearly ? "year" : "month",
        type: "subscription",
      },
    })

    // Step 2: Redirect to /payments/checkout
    onOpenChange && onOpenChange(!isOpen)
    push("/payments/checkout")
  }

  return (
    <>
      <ContactUs open={contactUsOpen} setOpen={setContactUsOpen} />
      <div className="-my-5 mx-auto h-screen w-[1000px] space-y-10 overflow-y-auto px-5 py-10">
        <div className="container mx-auto mt-5 space-y-2 text-center">
          <h1 className="text-2xl font-semibold leading-none tracking-tight text-primary md:text-5xl">
            <FormattedMessage id="pricingTitle" />
          </h1>
          <div className="gap-y-sm flex flex-wrap justify-center gap-x-1.5 text-xs leading-normal">
            <div className="text-muted-foreground">
              <FormattedMessage id="pricingSubtitle" />:
            </div>
            <ul className="flex flex-wrap items-center justify-center gap-x-1.5">
              <li className="gap-sm flex items-center font-medium">
                <span className="mr-1 text-green-500">✓</span>{" "}
                <FormattedMessage id="dailyBackups" />
              </li>
              <li className="gap-sm flex items-center font-medium">
                <span className="mr-1 text-green-500">✓</span>{" "}
                <FormattedMessage id="automaticUpdates" />
              </li>
              <li className="gap-sm flex items-center font-medium">
                <span className="mr-1 text-green-500">✓</span>{" "}
                <FormattedMessage id="enterpriseGradeSecurity" />
              </li>
            </ul>
          </div>
        </div>
        {/* Month year toggle switch */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-sm text-muted-foreground">
              <FormattedMessage id="monthly" />
            </span>
            <Switch
              className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-orange-500"
              checked={isYearly}
              onCheckedChange={(checked) => setIsYearly(checked)}
            />
            <span className="text-sm text-muted-foreground">
              <FormattedMessage id="yearly" />
            </span>
          </div>
        </div>
        <div className="grid h-fit items-stretch space-x-0 lg:grid-cols-[1fr,1fr,1fr]">
          {planKeys.map((key, index) => {
            const plan = subscriptionPlans[key]
            const isCurrentPlan = key === currentPlan

            return (
              <div
                key={index}
                className={cn(
                  "border-n flex flex-col space-y-1 p-0 pb-16 sm:flex",
                  index % 2 === 0 ? "pt-10" : "pt-2"
                )}
              >
                <Card
                  className={cn(
                    index % 2 === 0
                      ? "h-[500px] shadow-xl"
                      : "z-10 h-[600px] shadow-2xl",
                    "flex flex-col bg-accent"
                  )}
                >
                  <CardHeader>
                    <CardTitle className="text-2xl">
                      <FormattedMessage id={plan.name} />
                    </CardTitle>
                    <CardContent className="p-0 text-xl">
                      {key !== "PREMIUM" &&
                        (isYearly ? (
                          <>
                            <span className="text-primary">
                              ${" "}
                              <FormattedNumber
                                value={plan.priceYearly}
                                style="currency"
                                currency="CLP"
                              />
                            </span>{" "}
                            / <FormattedMessage id="yearly" />
                            <div className="mb-2 text-xs">
                              <span className="mr-1 text-muted-foreground line-through">
                                ${plan.priceMonthly}
                              </span>
                              ${plan.priceYearly / 12}{" "}
                              <span className="text-muted-foreground">
                                / <FormattedMessage id="month" /> -{" "}
                                <FormattedMessage id="yearly" />
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="text-primary">
                              $
                              <FormattedNumber
                                value={plan.priceMonthly}
                                style="currency"
                                currency="CLP"
                              />
                            </span>{" "}
                            /{" "}
                            <span className="text-xs">
                              <FormattedMessage id="perUser" />/
                              <FormattedMessage id="month" />
                            </span>
                            <div className="mb-2 text-xs">
                              <span className="mr-1 text-muted-foreground line-through">
                                ${plan.priceMonthly}
                              </span>
                              ${plan.priceYearly / 12}{" "}
                              <span className="text-muted-foreground">
                                / <FormattedMessage id="month" /> -{" "}
                                <FormattedMessage id="yearly" />
                              </span>
                            </div>
                          </>
                        ))}
                    </CardContent>
                  </CardHeader>
                  <CardContent className="grow">
                    <ul className="space-y-2">
                      {plan.features.map((featureKey, index) => (
                        <li key={index}>
                          <span className="text-green-500">✓</span>{" "}
                          <FormattedMessage id={featureKey} />
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="mt-auto flex items-center justify-center">
                    {key === "PREMIUM" ? (
                      <Button
                        className=" bg-orange-600 text-white hover:bg-orange-500"
                        type="button"
                        onClick={() => setContactUsOpen(true)}
                      >
                        <FormattedMessage id="contactUs" />
                      </Button>
                    ) : (
                      <Button
                        className=" bg-orange-600 text-white hover:bg-orange-500"
                        type="button"
                        disabled={isCurrentPlan || currentPlan === "BUSINESS"}
                        onClick={() => handlePlanSelection(key)}
                      >
                        {isCurrentPlan ? (
                          <FormattedMessage id="currentPlan" />
                        ) : (
                          <FormattedMessage id="upgrade" />
                        )}
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default function SubscriptionPlans() {
  return <SubscriptionPlansMain />
}
